import { Box, Stack, Typography, useTheme } from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { findAlternateId } from "encamp-shared/src/facilityAlternateId/findAlternateId";
import { GetWasteShipmentQuery } from "generated-graphql/graphql";

import { WasteShipmentStatusChip } from "./WasteShipmentStatusChip";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;
type Facility = Shipment["facility"];

/**
 * Which site the shipment left, as a reader identifies a site: its name and
 * internal code, the EPA ID it is regulated under, and its address.
 *
 * Segments that are missing are dropped rather than rendered as placeholders —
 * a facility with no customer code should read as its name, not as its name
 * followed by an empty bracket.
 */
function facilityIdentityLine(facility: Facility): string {
  if (!facility) {
    return "";
  }
  const code = facility.customerFacilityId;
  const epaId = findAlternateId(facility.facilityAlternateIds ?? [], ["EPA"])?.value;
  const address = [
    facility.streetAddress1,
    facility.city,
    [facility.state, facility.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  return [
    code ? `${facility.name} (${code})` : facility.name,
    epaId ? `EPA ID: ${epaId}` : null,
    address || null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * The shipment's identity, pinned above the page.
 *
 * It stays put because the page is long and read in two directions: the
 * contents grid is the tallest thing on it, and a reader deep in a stream's
 * containers still needs to know which shipment they are editing — and to be
 * able to commit it without scrolling back.
 *
 * The actions live here for that second reason. This is not the floating save
 * bar the design deliberately avoids: it is the page's own header, which
 * happens to stay visible, rather than a bar hovering over the content.
 *
 * There is no edit control. An editable shipment is simply editable, so there
 * is no mode to enter — the fields are fields and the container rows carry
 * their own actions.
 */
export function ShipmentPageHeader({
  shipment,
  actions,
}: {
  shipment: Shipment;
  /** Save/Discard when there is something to commit, plus the overflow menu. */
  actions: React.ReactNode;
}) {
  const theme = useTheme();
  const identity = facilityIdentityLine(shipment.facility);

  return (
    <Box
      sx={{
        position: "sticky",
        // Directly under the app bar, which is itself fixed — any gap here
        // shows a strip of scrolling content between the two.
        top: { xs: theme.spacing(xsHeight), md: theme.spacing(mdHeight) },
        zIndex: (t) => t.zIndex.appBar - 1,
        backgroundColor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        px: 3,
        py: 1.5,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
            <Typography component="h1" variant="h6" noWrap sx={{ minWidth: 0 }}>
              Shipment {shipment.manifestRequestNumber}
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
              <WasteShipmentStatusChip status={shipment.status} />
            </Box>
          </Stack>
          {identity && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {identity}
            </Typography>
          )}
        </Box>
        {/* Never shrinks: the identity beside it is the part that can afford an
            ellipsis, and a Save that wraps or clips is worse than a truncated
            address. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0, pt: 0.25 }}>
          {actions}
        </Stack>
      </Stack>
    </Box>
  );
}
